import {
  TextField,
  emailRegex,
  passwordRegex,
  telRegex,
  urlRegex,
} from '@iheartjane/reefer';
import type { TextFieldProps } from '@iheartjane/reefer';
import { forwardRef } from 'react';

import type { FormFieldProps } from '../formField.types';
import { FormFieldWrapper } from '../formFieldWrapper/formFieldWrapper';

export interface FormTextFieldProps
  extends
    Omit<TextFieldProps, 'children' | 'onUpdate'>,
    Omit<FormFieldProps, 'max' | 'min'> {}

/**
 * Text fields allow users to enter text into a UI.
 *
 * Use this component *within forms* for inputs of `type`:
 * `email`, `password`, `tel`, `text` or `url`.
 *
 * _Field validations supported:_ `required`, `minLength`, `maxLength`, `pattern`, `validate`.
 *
 * Default pattern validations are configured for `email`, `password`, `tel` and `url` fields.
 * If necessary, these default pattern validations can be overridden with the `pattern` and `errorMessage` props.
 *
 * _Password validation_: requires a min of 10 characters, at least 1 number, and at least one 1 special chanracter
 *
 * _Tel validation_: Only supports US phone numbers.
 *
 * **NOTE: DO NOT USE THIS COMPONENT OUTSIDE FORMS**
 *
 * _For a similar component for use outside forms, see [`TextField`](/story/components-forms-fields-textfield--default)._
 */
export const FormTextField = forwardRef<HTMLInputElement, FormTextFieldProps>(
  function FormTextField(
    {
      autoCapitalize,
      autocomplete,
      autocompleteFooter,
      autocompleteOptions,
      autoFocus = false,
      borderRadius = 'sm',
      clearIcon,
      defaultValue = '',
      disabled = false,
      enterKeyHint,
      helperText,
      endIcon,
      errorMessage,
      errorOnBlur = false,
      label,
      labelHidden = false,
      maxLength,
      minLength,
      name,
      onBlur,
      onChange,
      onFocus,
      onSelection,
      pattern,
      placeholder,
      readOnly = false,
      required = false,
      requiredMessage,
      shouldUnregister,
      'data-testid': testId,
      startIcon,
      type = 'text',
      validate,
      width = 'auto',
      ...props
    }: FormTextFieldProps,
    forwardedRef
  ) {
    let myPattern;
    let myErrorMessage;

    if (type === 'email') {
      myPattern = emailRegex;
      myErrorMessage = 'Please enter an email address.';
    } else if (type === 'url') {
      myPattern = urlRegex;
      myErrorMessage = 'Please enter a URL.';
    } else if (type === 'tel') {
      myPattern = telRegex;
      myErrorMessage = 'Please enter a valid US phone number.';
    } else if (type === 'password') {
      myPattern = passwordRegex;
      myErrorMessage =
        'Please enter a password with a minimum of 10 characters.';
    }

    myPattern = pattern ? pattern : myPattern;
    myErrorMessage = pattern ? errorMessage : myErrorMessage;

    return (
      <FormFieldWrapper
        defaultValue={defaultValue}
        errorMessage={myErrorMessage}
        errorOnBlur={errorOnBlur}
        maxLength={maxLength}
        minLength={minLength}
        name={name}
        pattern={myPattern}
        render={({ children, field, invalid }) => (
          <TextField
            aria-invalid={invalid}
            autoCapitalize={autoCapitalize}
            autocomplete={autocomplete}
            autocompleteFooter={autocompleteFooter}
            autocompleteOptions={autocompleteOptions}
            autoFocus={autoFocus}
            borderRadius={borderRadius}
            clearIcon={clearIcon}
            defaultValue={defaultValue}
            disabled={disabled}
            endIcon={endIcon}
            enterKeyHint={enterKeyHint}
            helperText={helperText}
            label={label}
            labelHidden={labelHidden}
            maxLength={maxLength}
            minLength={minLength}
            name={field.name}
            onBlur={(value) => {
              onBlur && onBlur(value);
              field.onBlur();
            }}
            onChange={(value) => {
              onChange && onChange(value);
              field.onChange(value);
            }}
            onFocus={onFocus}
            onSelection={onSelection}
            onUpdate={onChange}
            pattern={pattern}
            placeholder={placeholder}
            readOnly={readOnly}
            required={required}
            ref={(el) => {
              field.ref(el);
              if (forwardedRef) {
                if (typeof forwardedRef === 'function') {
                  forwardedRef(el);
                } else {
                  forwardedRef.current = el;
                }
              }
            }}
            data-testid={testId}
            startIcon={startIcon}
            type={type}
            value={field.value}
            width={width}
            {...props}
          >
            {children}
          </TextField>
        )}
        required={required}
        requiredMessage={requiredMessage}
        shouldUnregister={shouldUnregister}
        validate={validate}
      />
    );
  }
);
